/**
 * Troca entre o tema escuro e o claro.
 *
 * O ícone mostra o destino, não o estado: no escuro aparece o sol, porque é
 * para lá que o clique leva. Mostrar o estado atual é a outra convenção
 * possível, e as duas confundem sozinhas — o que resolve é o rótulo acessível
 * dizer a ação por extenso, que é o que o leitor de tela anuncia e o que
 * aparece ao parar o mouse em cima.
 */

import { useSyncExternalStore } from 'react';
import { alternarTema, assinarTema, lerTema } from '@/lib/tema';
import { cn } from '@/lib/cn';

export function useTema() {
  /*
   * O terceiro argumento é o valor do servidor. Aqui ele cobre a primeira
   * renderização do React antes de tocar no DOM: como o `<html>` já nasce com o
   * tema certo pelo script do `index.html`, o escuro como palpite nunca chega a
   * ser pintado errado.
   */
  return useSyncExternalStore(assinarTema, lerTema, () => 'escuro' as const);
}

export function BotaoTema({ className }: { className?: string }) {
  const tema = useTema();
  const paraOClaro = tema === 'escuro';
  const rotulo = paraOClaro ? 'Mudar para o tema claro' : 'Mudar para o tema escuro';

  return (
    <button
      type="button"
      onClick={alternarTema}
      title={rotulo}
      aria-label={rotulo}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-(--vidro) text-paper transition-colors duration-[--duration-quick] hover:bg-brand hover:text-black',
        className,
      )}
    >
      {paraOClaro ? <IconeSol /> : <IconeLua />}
    </button>
  );
}

function IconeSol() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function IconeLua() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    </svg>
  );
}
